import { useState } from 'react';
import { Button, Input } from '@mlm/ui';
import type { CashierDiscountConfig } from '../../lib/api';
import { ManagerPinForm } from './manager-pin-form';

/**
 * The cashier's typed discount: a percent and / or an amount, with a reason.
 * Within the branch caps it needs nobody; above either cap (or at any size on a
 * branch set to presets only) it needs a manager's PIN before the sale can be
 * charged. Sits on top of a preset or code when one is applied.
 */
export function ManagerDiscountSection({
  cfg,
  pct,
  amount,
  reason,
  approvalChip,
  setPct,
  setAmount,
  setReason,
  setApprovalChip,
  aboveCap,
}: {
  cfg: CashierDiscountConfig;
  pct: string;
  amount: string;
  reason: string;
  approvalChip: string | null;
  setPct: (v: string) => void;
  setAmount: (v: string) => void;
  setReason: (v: string) => void;
  setApprovalChip: (v: string | null) => void;
  /** This discount needs a manager's approval before charging. */
  aboveCap: boolean;
}) {
  const [pinMode, setPinMode] = useState(false);
  const presetsOnly = cfg.mode === 'presets_only';

  return (
    <div className="rounded-[10px] border p-4 space-y-3">
      <div className="text-lg font-semibold">
        {presetsOnly ? 'Manager discount' : 'Cashier discount'}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">Percent</label>
          <Input
            className="h-12 text-base"
            type="number"
            inputMode="decimal"
            value={pct}
            onChange={(e) => {
              setPct(e.target.value);
              setApprovalChip(null);
            }}
            placeholder="0"
            min={0}
            max={100}
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">Amount (₱)</label>
          <Input
            className="h-12 text-base"
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setApprovalChip(null);
            }}
            placeholder="0"
            min={0}
          />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-sm text-muted-foreground">Reason</label>
        <Input
          className="h-12 text-base"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. damaged box, regular customer"
        />
      </div>

      <div className="text-sm text-muted-foreground">
        {presetsOnly ? (
          'A typed discount needs a manager PIN at this branch.'
        ) : (
          <>
            Cap without approval: {cfg.max_pct_without_approval}% / ₱
            {cfg.max_amount_without_approval.toFixed(2)}.
          </>
        )}
      </div>

      {aboveCap &&
        (approvalChip ? (
          <div className="text-sm text-success">✓ Manager approval recorded.</div>
        ) : !pinMode ? (
          <Button variant="outline" className="h-12" onClick={() => setPinMode(true)}>
            {presetsOnly ? 'Manager PIN required' : 'Above cap: manager PIN required'}
          </Button>
        ) : (
          <ManagerPinForm
            onApproved={(chip) => {
              setApprovalChip(chip);
              setPinMode(false);
            }}
            onCancel={() => setPinMode(false)}
          />
        ))}
    </div>
  );
}
